import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  CurrencyDollarIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  XMarkIcon,
  BanknotesIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { toast } from 'react-toastify';

const AdminCommissionSettings = () => {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingInlineId, setEditingInlineId] = useState(null);
  const [inlineFormData, setInlineFormData] = useState({});
  const [formData, setFormData] = useState({
    construction_type: '',
    min_amount: '',
    rate_percentage: ''
  });
  const [accountInfo, setAccountInfo] = useState({
    commission_bank: '',
    commission_account: '',
    commission_depositor: ''
  });

  // 수수료율 목록 조회
  const { data: commissionData, isLoading } = useQuery(
    'commission-rates',
    () => api.get('/admin/commission-rates').then(res => res.data)
  );

  const commissionRates = commissionData?.rates || [];
  const availableTypes = commissionData?.availableTypes || [];

  // 계좌 정보 조회
  const { data: settings } = useQuery(
    'admin-settings',
    () => api.get('/admin/settings').then(res => res.data),
    {
      refetchOnMount: 'always',  // 페이지 마운트 시 항상 새로고침
      staleTime: 0,  // 데이터를 즉시 stale로 표시
      cacheTime: 0,  // 캐시 비활성화
      onSuccess: (data) => {
        console.log('Settings loaded:', data);
        setAccountInfo({
          commission_bank: data.commission_bank || '',
          commission_account: data.commission_account || '',
          commission_depositor: data.commission_depositor || ''
        });
      }
    }
  );

  // 공사 유형별 그룹핑
  const groupedRates = commissionRates?.reduce((acc, rate) => {
    if (!acc[rate.construction_type]) {
      acc[rate.construction_type] = [];
    }
    acc[rate.construction_type].push(rate);
    return acc;
  }, {}) || {};

  // 수수료율 추가
  const addMutation = useMutation(
    (data) => api.post('/admin/commission-rates', data),
    {
      onSuccess: () => {
        toast.success('수수료율이 추가되었습니다.');
        queryClient.invalidateQueries('commission-rates');
        setShowAddForm(false);
        setFormData({ construction_type: '', min_amount: '', rate_percentage: '' });
      },
      onError: (error) => {
        console.error('수수료율 추가 실패:', error);
        toast.error(error.response?.data?.message || '수수료율 추가에 실패했습니다.');
      }
    }
  );

  // 수수료율 수정
  const updateMutation = useMutation(
    ({ id, data }) => api.put(`/admin/commission-rates/${id}`, data),
    {
      onSuccess: () => {
        toast.success('수수료율이 수정되었습니다.');
        queryClient.invalidateQueries('commission-rates');
        setIsEditing(null);
        setShowAddForm(false);
        setEditingInlineId(null);
        setFormData({ construction_type: '', min_amount: '', rate_percentage: '' });
      },
      onError: (error) => {
        console.error('수수료율 수정 실패:', error);
        toast.error(error.response?.data?.message || '수수료율 수정에 실패했습니다.');
      }
    }
  );

  // 수수료율 삭제
  const deleteMutation = useMutation(
    (id) => api.delete(`/admin/commission-rates/${id}`),
    {
      onSuccess: () => {
        toast.success('수수료율이 삭제되었습니다.');
        queryClient.invalidateQueries('commission-rates');
      },
      onError: (error) => {
        console.error('수수료율 삭제 실패:', error);
        toast.error(error.response?.data?.message || '수수료율 삭제에 실패했습니다.');
      }
    }
  );

  // 계좌 정보 업데이트
  const updateAccountMutation = useMutation(
    (data) => api.put('/admin/settings', data),
    {
      onSuccess: () => {
        toast.success('계좌 정보가 저장되었습니다.');
        queryClient.invalidateQueries('admin-settings');
      },
      onError: (error) => {
        console.error('계좌 정보 저장 실패:', error);
        toast.error('계좌 정보 저장에 실패했습니다.');
      }
    }
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount) + '원';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      min_amount: parseInt(formData.min_amount) || 0,
      rate_percentage: parseFloat(formData.rate_percentage)
    };
    
    if (isEditing) {
      updateMutation.mutate({ id: isEditing, data });
    } else {
      addMutation.mutate(data);
    }
  };

  const handleEdit = (rate) => {
    setIsEditing(rate.id);
    setFormData({
      construction_type: rate.construction_type,
      min_amount: rate.min_amount.toString(),
      rate_percentage: rate.rate_percentage.toString()
    });
    setShowAddForm(true);
  };

  const handleInlineEdit = (rate) => {
    setEditingInlineId(rate.id);
    setInlineFormData({
      construction_type: rate.construction_type,
      min_amount: rate.min_amount.toString(),
      rate_percentage: rate.rate_percentage.toString()
    });
  };

  const handleInlineSave = async (id) => {
    const data = {
      construction_type: inlineFormData.construction_type,
      min_amount: parseInt(inlineFormData.min_amount) || 0,
      rate_percentage: parseFloat(inlineFormData.rate_percentage)
    };
    
    if (isNaN(data.rate_percentage) || data.rate_percentage < 0 || data.rate_percentage > 100) {
      toast.error('수수료율은 0~100 사이의 숫자여야 합니다.');
      return;
    }
    
    updateMutation.mutate({ id, data });
  };

  const handleInlineCancel = () => {
    setEditingInlineId(null);
    setInlineFormData({});
  };

  const handleCancel = () => {
    setIsEditing(null);
    setShowAddForm(false);
    setFormData({ construction_type: '', min_amount: '', rate_percentage: '' });
  };

  // 키보드 단축키 처리
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (editingInlineId) {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleInlineSave(editingInlineId);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          handleInlineCancel();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingInlineId, inlineFormData]);

  const handleAccountInfoSubmit = (e) => {
    e.preventDefault();
    updateAccountMutation.mutate(accountInfo);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">수수료 설정을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">수수료 설정 관리</h1>
        <p className="mt-2 text-gray-600">
          공사 유형별 수수료율과 최소 적용 금액을 관리합니다.
        </p>
      </div>

      <div className="space-y-6">
        {/* 계좌 정보 설정 */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <BanknotesIcon className="h-5 w-5 text-gray-600" />
              기본 납부 계좌 정보
            </h2>
          </div>
          
          <form onSubmit={handleAccountInfoSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  은행명
                </label>
                <input
                  type="text"
                  value={accountInfo.commission_bank}
                  onChange={(e) => setAccountInfo({ ...accountInfo, commission_bank: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="예: 국민은행"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  계좌번호
                </label>
                <input
                  type="text"
                  value={accountInfo.commission_account}
                  onChange={(e) => setAccountInfo({ ...accountInfo, commission_account: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="예: 123-456-789012"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  예금주
                </label>
                <input
                  type="text"
                  value={accountInfo.commission_depositor}
                  onChange={(e) => setAccountInfo({ ...accountInfo, commission_depositor: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="예: (주)건기"
                  required
                />
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={updateAccountMutation.isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <CheckIcon className="h-4 w-4" />
                계좌 정보 저장
              </button>
            </div>
          </form>
          
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600">
              <span className="font-medium">안내:</span> 여기에 설정된 계좌 정보는 모든 소장에게 기본으로 표시됩니다.
              소장별로 다른 계좌를 사용하는 경우 소장 관리에서 개별 설정할 수 있습니다.
            </p>
          </div>
        </div>

        {/* 수수료율 추가 버튼 */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">수수료율 설정</h2>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              새 설정 추가
            </button>
          </div>

          {/* 경고 메시지 */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800">수수료율 설정 안내</p>
                <ul className="text-xs text-yellow-700 mt-1 space-y-1">
                  <li>• 동일 공사 유형에서 높은 금액 구간일수록 낮은 수수료율을 적용합니다</li>
                  <li>• 최소 적용 금액은 해당 수수료율이 적용되는 최소 계약 금액입니다</li>
                  <li>• 프로젝트에 여러 공사 유형이 있을 경우 각각 계산하여 합산됩니다</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 추가/수정 폼 */}
          {showAddForm && (
            <form onSubmit={handleSubmit} className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    공사 유형
                  </label>
                  <select
                    value={formData.construction_type}
                    onChange={(e) => setFormData({ ...formData, construction_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">선택하세요</option>
                    {availableTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    최소 적용 금액 (원)
                  </label>
                  <input
                    type="number"
                    value={formData.min_amount}
                    onChange={(e) => setFormData({ ...formData, min_amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                    min="0"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    수수료율 (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.rate_percentage}
                    onChange={(e) => setFormData({ ...formData, rate_percentage: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                    min="0"
                    max="100"
                    required
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  <XMarkIcon className="h-4 w-4" />
                  취소
                </button>
                <button
                  type="submit"
                  disabled={addMutation.isLoading || updateMutation.isLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  <CheckIcon className="h-4 w-4" />
                  {isEditing ? '수정' : '추가'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* 공사 유형별 수수료율 목록 */}
        {Object.entries(groupedRates).map(([constructionType, rates]) => (
          <div key={constructionType} className="bg-white rounded-2xl shadow-lg p-6 mb-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
              {constructionType}
            </h3>
            
            <div className="space-y-3">
              {rates
                .sort((a, b) => a.min_amount - b.min_amount)
                .map((rate, index) => (
                <div key={rate.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  {editingInlineId === rate.id ? (
                    // 인라인 편집 모드
                    <>
                      <div className="flex-1 flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={inlineFormData.min_amount || ''}
                            onChange={(e) => setInlineFormData({ ...inlineFormData, min_amount: e.target.value })}
                            className="w-32 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            placeholder="최소금액"
                            min="0"
                          />
                          <span className="text-sm text-gray-600">원 이상</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            step="0.01"
                            value={inlineFormData.rate_percentage || ''}
                            onChange={(e) => setInlineFormData({ ...inlineFormData, rate_percentage: e.target.value })}
                            className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-bold"
                            placeholder="수수료율"
                            min="0"
                            max="100"
                          />
                          <span className="text-sm text-gray-600">%</span>
                        </div>
                        <div className="text-xs text-gray-500 bg-yellow-100 px-2 py-1 rounded">
                          Enter: 저장 / ESC: 취소
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleInlineSave(rate.id)}
                          className="p-2 text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                          title="저장 (Enter)"
                        >
                          <CheckIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={handleInlineCancel}
                          className="p-2 text-white bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors"
                          title="취소 (ESC)"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </>
                  ) : (
                    // 일반 표시 모드
                    <>
                      <div className="flex-1">
                        <div className="flex items-center gap-4">
                          <div className="text-sm text-gray-600">
                            {index === 0 ? '최소' : formatCurrency(rate.min_amount)} 이상
                          </div>
                          <div className="text-lg font-bold text-blue-600">
                            {rate.rate_percentage}%
                          </div>
                          {rate.min_amount > 0 && (
                            <div className="text-xs text-gray-500">
                              ({formatCurrency(rate.min_amount)}부터)
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleInlineEdit(rate)}
                          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="빠른 수정"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm('이 수수료 설정을 삭제하시겠습니까?')) {
                              deleteMutation.mutate(rate.id);
                            }
                          }}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="삭제"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* 수수료 계산 예시 */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">📊 수수료 계산 방식 설명</h3>
          
          <div className="bg-amber-50 rounded-xl p-4 mb-4">
            <p className="text-sm font-bold text-amber-800 mb-2">⚠️ 중요: 누진제 구간별 계산 방식</p>
            <div className="space-y-2 text-sm text-amber-700">
              <p>• 수수료는 <strong>구간별로 다른 요율</strong>이 적용되는 누진제입니다</p>
              <p>• 각 구간의 금액에만 해당 구간의 수수료율이 적용됩니다</p>
              <p>• 최종 수수료는 모든 구간의 수수료를 합산한 금액입니다</p>
            </div>
          </div>

          <div className="bg-blue-50 rounded-xl p-4 mb-4">
            <p className="text-sm font-bold text-blue-800 mb-3">💡 계산 예시 1: 인테리어 1억원</p>
            <div className="space-y-2 text-sm text-blue-700">
              <p className="font-medium">설정된 구간:</p>
              <p className="ml-4">• 0원 ~ 500만원: 18%</p>
              <p className="ml-4">• 500만원 ~ 3,000만원: 15%</p>
              <p className="ml-4">• 3,000만원 ~ 1억원: 12%</p>
              <p className="ml-4">• 1억원 이상: 10%</p>
              
              <p className="font-medium mt-3">계산 과정:</p>
              <p className="ml-4">① 첫 500만원 × 18% = <strong>90만원</strong></p>
              <p className="ml-4">② 다음 2,500만원 × 15% = <strong>375만원</strong></p>
              <p className="ml-4">③ 다음 7,000만원 × 12% = <strong>840만원</strong></p>
              <p className="ml-4 font-bold text-blue-900 mt-2">✅ 총 수수료: 1,305만원</p>
            </div>
          </div>

          <div className="bg-green-50 rounded-xl p-4 mb-4">
            <p className="text-sm font-bold text-green-800 mb-3">💡 계산 예시 2: 철거공사 3,000만원</p>
            <div className="space-y-2 text-sm text-green-700">
              <p className="font-medium">설정된 구간:</p>
              <p className="ml-4">• 0원 ~ 200만원: 25%</p>
              <p className="ml-4">• 200만원 ~ 1,500만원: 22%</p>
              <p className="ml-4">• 1,500만원 ~ 5,000만원: 18%</p>
              
              <p className="font-medium mt-3">계산 과정:</p>
              <p className="ml-4">① 첫 200만원 × 25% = <strong>50만원</strong></p>
              <p className="ml-4">② 다음 1,300만원 × 22% = <strong>286만원</strong></p>
              <p className="ml-4">③ 다음 1,500만원 × 18% = <strong>270만원</strong></p>
              <p className="ml-4 font-bold text-green-900 mt-2">✅ 총 수수료: 606만원</p>
            </div>
          </div>

          <div className="bg-purple-50 rounded-xl p-4 mb-4">
            <p className="text-sm font-bold text-purple-800 mb-3">💡 계산 예시 3: 소액 공사 100만원</p>
            <div className="space-y-2 text-sm text-purple-700">
              <p className="font-medium">라인마킹/도색 100만원 계약 시:</p>
              <p className="ml-4">• 0원 ~ 50만원: 28%</p>
              <p className="ml-4">• 50만원 ~ 500만원: 25%</p>
              
              <p className="font-medium mt-3">계산 과정:</p>
              <p className="ml-4">① 첫 50만원 × 28% = <strong>14만원</strong></p>
              <p className="ml-4">② 다음 50만원 × 25% = <strong>12.5만원</strong></p>
              <p className="ml-4 font-bold text-purple-900 mt-2">✅ 총 수수료: 26.5만원</p>
            </div>
          </div>

          <div className="bg-gray-100 rounded-xl p-4">
            <p className="text-sm font-bold text-gray-800 mb-2">📌 복수 공사 유형의 경우</p>
            <div className="space-y-2 text-sm text-gray-700">
              <p>• 여러 공사 유형이 포함된 경우, 계약 금액을 공사 수로 균등 분할</p>
              <p>• 각 공사 유형별로 개별 계산 후 합산</p>
              <p className="font-medium mt-2">예: 인테리어 + 철거공사 총 6,000만원</p>
              <p className="ml-4">→ 인테리어 3,000만원 수수료 + 철거공사 3,000만원 수수료</p>
            </div>
          </div>

          <div className="bg-red-50 rounded-xl p-4 mt-4">
            <p className="text-sm font-bold text-red-800 mb-2">⚠️ 주의사항</p>
            <div className="space-y-1 text-sm text-red-700">
              <p>• min_amount(최소금액)는 해당 수수료율이 시작되는 금액입니다</p>
              <p>• 0원 구간이 없으면 소액 공사는 수수료가 계산되지 않습니다</p>
              <p>• 구간 설정 시 연속성을 확인하세요 (구간 사이 공백 없도록)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCommissionSettings;